// Single source of truth for Waymining company offerings.
// Reused by the Products & Services dropdown, the company home services grid,
// and the per-service pages (/services/:slug).

export interface ServiceBenefit {
  title: string;
  description: string;
}

export interface Service {
  slug: string;
  name: string;
  tagline: string;
  /** lucide-react icon name, resolved via the iconMap in components */
  icon: string;
  summary: string;
  description: string;
  features: string[];
  benefits: ServiceBenefit[];
}

export interface Product {
  name: string;
  path: string;
  tagline: string;
  icon: string;
}

export const services: Service[] = [
  {
    slug: 'collision-avoidance',
    name: 'Collision Avoidance Systems',
    tagline: 'Keep operators, equipment and pedestrians safe',
    icon: 'ShieldAlert',
    summary:
      'Next-generation anti-collision technology that outperforms legacy systems at a lower total cost of ownership.',
    description:
      'We design, supply and integrate next-generation collision avoidance systems for mining fleets. Compared with legacy hardware, our solution lowers total cost of ownership while improving detection accuracy and reducing nuisance alarms — protecting operators, equipment and pedestrians across the mine site.',
    features: [
      'Real-time proximity detection for vehicles and pedestrians',
      'Lower total cost of ownership vs. legacy systems',
      'In-cab visual and audible operator alerts',
      'Compliant with mine safety regulations',
      'Fleet-wide reporting and incident analytics',
    ],
    benefits: [
      {
        title: 'Lower Total Cost of Ownership',
        description: 'Outperforms legacy hardware while costing less to acquire and maintain.',
      },
      {
        title: 'Fewer Nuisance Alarms',
        description: 'Improved detection accuracy keeps operators trusting the system instead of ignoring it.',
      },
      {
        title: 'Regulatory Compliance',
        description: 'Built to meet mine safety regulations out of the box.',
      },
    ],
  },
  {
    slug: 'fatigue-detection',
    name: 'Driver Fatigue Detection Cameras',
    tagline: 'Catch fatigue and distraction before incidents happen',
    icon: 'Camera',
    summary:
      'In-cab AI cameras that detect operator fatigue and distraction in real time and trigger instant alerts.',
    description:
      'Our in-cab fatigue detection cameras use AI to monitor operator alertness in real time, identifying micro-sleeps, eye closure and distraction. The moment fatigue is detected, the system triggers an in-cab alert and notifies the control room, preventing incidents before they happen.',
    features: [
      'AI-based eye and head-pose monitoring',
      'Instant in-cab alerts on fatigue or distraction',
      'Control-room notifications and event logging',
      'Camera specification and supply to mine requirements',
      'Day and night infrared operation',
    ],
    benefits: [
      {
        title: 'Incidents Prevented Before They Happen',
        description: 'Real-time detection triggers alerts the moment fatigue appears.',
      },
      {
        title: 'Day and Night Coverage',
        description: 'Infrared operation keeps monitoring accurate around the clock.',
      },
      {
        title: 'Full Visibility for Control Rooms',
        description: 'Every fatigue event is logged and pushed to dispatch instantly.',
      },
    ],
  },
  {
    slug: 'drone-dust-suppression',
    name: 'Drone Dust Suppression',
    tagline: 'Autonomous spraying with less water and less risk',
    icon: 'Plane',
    summary:
      'Autonomous drone-based water spraying for dust suppression and area treatment.',
    description:
      'We provide a complete drone-based dust suppression service — including aircraft, payloads and trained operation. Autonomous spraying treats haul roads, stockpiles and active areas with precision, reducing manual operations, water waste and exposure of personnel to hazardous zones.',
    features: [
      'Autonomous, programmable spraying routes',
      'Precision dust suppression for roads and stockpiles',
      'Reduced water consumption vs. manual bowsers',
      'Operation and maintenance handled by our team',
      'Lower personnel exposure in hazardous areas',
    ],
    benefits: [
      {
        title: 'Less Water, Same Coverage',
        description: 'Precision spraying treats only what needs it, cutting consumption vs. manual bowsers.',
      },
      {
        title: 'Personnel Kept Out of Harm\'s Way',
        description: 'Autonomous routes remove people from hazardous dust zones.',
      },
      {
        title: 'Fully Managed Service',
        description: 'We handle the aircraft, payloads and operation so your team doesn\'t have to.',
      },
    ],
  },
  {
    slug: 'control-room',
    name: 'Control Room Modernization',
    tagline: 'Cloud-based, remote-capable operations centers',
    icon: 'Monitor',
    summary:
      'Strategies and technology to modernize or replace traditional control rooms.',
    description:
      'We define and deliver strategies to modernize or fully replace traditional mine control rooms with cloud-based, remote-capable operations centers. The result is lower operating cost, improved resilience, and the flexibility to monitor and dispatch operations from anywhere.',
    features: [
      'Assessment of existing control-room operations',
      'Migration strategy to cloud-based dispatch',
      'Remote and distributed operations capability',
      'Integration with fleet and safety systems',
      'Reduced operating cost and improved resilience',
    ],
    benefits: [
      {
        title: 'Operate From Anywhere',
        description: 'Cloud-based dispatch removes the dependency on a single physical room.',
      },
      {
        title: 'Lower Operating Cost',
        description: 'Modern infrastructure reduces the overhead of maintaining legacy control rooms.',
      },
      {
        title: 'Built-In Resilience',
        description: 'Distributed operations continue even if one site goes offline.',
      },
    ],
  },
  {
    slug: 'it-connectivity',
    name: 'Industrial IT & Connectivity',
    tagline: 'Contracts, licensing and reliable connectivity',
    icon: 'Network',
    summary:
      'Industrial IT contract management, software licensing, and reliable internet connectivity.',
    description:
      'We manage the industrial IT backbone that mining operations depend on — consolidating and managing supplier contracts, optimizing software licensing costs, and delivering reliable internet connectivity for mine sites and residential condominiums.',
    features: [
      'Industrial IT contract compilation and management',
      'Software licensing cost optimization',
      'Internet connectivity for sites and condominiums',
      'Vendor and validity tracking',
      'Ongoing support and service management',
    ],
    benefits: [
      {
        title: 'One Point of Accountability',
        description: 'All supplier contracts and licensing consolidated and tracked in one place.',
      },
      {
        title: 'Lower Software Spend',
        description: 'Licensing costs reviewed and optimized against actual usage.',
      },
      {
        title: 'Connectivity That Doesn\'t Drop',
        description: 'Reliable internet for both mine sites and residential condominiums.',
      },
    ],
  },
  {
    slug: 'access-manager',
    name: 'Access Manager',
    tagline: 'Unified, IP-based access control',
    icon: 'KeyRound',
    summary:
      'Centralized access control — manage doors, credentials and permissions across every site from one open platform.',
    description:
      'Access Manager is a unified, open-architecture access control platform in the same class as Genetec Synergis. It secures doors, gates and restricted zones across all your sites from a single interface, supports a wide range of credential technologies, and unifies with CCTV Manager so access events and live video work together in one operator view.',
    features: [
      'Centralized management of doors, zones and credentials',
      'Cards, PIN, mobile and biometric credential support',
      'Role-based permissions and time-based access schedules',
      'Real-time door monitoring and alarm handling',
      'Open architecture with leading IP controller hardware',
      'Full audit trails and compliance reporting',
      'Unified with CCTV Manager for access-linked video',
    ],
    benefits: [
      {
        title: 'One Interface, Every Site',
        description: 'Manage doors, credentials and permissions centrally instead of site by site.',
      },
      {
        title: 'Any Credential, One Platform',
        description: 'Cards, PIN, mobile and biometric support without hardware lock-in.',
      },
      {
        title: 'Video and Access, Unified',
        description: 'Works together with CCTV Manager so operators see access and footage side by side.',
      },
    ],
  },
  {
    slug: 'cctv-manager',
    name: 'CCTV Manager',
    tagline: 'Enterprise video management & surveillance',
    icon: 'Cctv',
    summary:
      'Enterprise video management — live monitoring, recording and investigation across all cameras and sites.',
    description:
      'CCTV Manager is an enterprise video management system (VMS) in the same class as Genetec Omnicast. It brings live monitoring, recording, playback and investigation together for IP and analog cameras across all sites, scales from a single building to a distributed operation, and unifies with Access Manager for a single physical-security platform.',
    features: [
      'Live monitoring and multi-site video walls',
      'Continuous and event-based recording with retention policies',
      'Fast playback, bookmarking and investigation tools',
      'Support for major IP and analog camera brands',
      'Smart search, motion and analytics integration',
      'Scalable from single site to distributed enterprise',
      'Unified with Access Manager for end-to-end security',
    ],
    benefits: [
      {
        title: 'Scales With You',
        description: 'From a single building to a distributed multi-site operation on the same platform.',
      },
      {
        title: 'Faster Investigations',
        description: 'Smart search and bookmarking cut down time spent reviewing footage.',
      },
      {
        title: 'Open Camera Support',
        description: 'Works with major IP and analog camera brands, not just one vendor.',
      },
    ],
  },
  {
    slug: 'plant-processing-ai-automation',
    name: 'Plant Processing AI Automation',
    tagline: 'AI-driven optimization for processing plants',
    icon: 'BrainCircuit',
    summary:
      'AI that monitors and optimizes mineral processing in real time — stabilizing throughput, recovery and quality.',
    description:
      'We apply artificial intelligence and advanced process control to mineral processing plants — crushing, milling, flotation and beyond. By learning from live sensor data, our models stabilize and optimize the process in real time, lifting throughput and recovery, reducing reagent and energy consumption, and holding product quality steady around the clock.',
    features: [
      'Real-time optimization of throughput, recovery and grade',
      'AI-based advanced process control across plant circuits',
      'Reagent, water and energy consumption reduction',
      'Predictive models for equipment and process upsets',
      'Integration with existing plant control and historian systems',
      'Operator decision support and autonomous setpoint control',
    ],
    benefits: [
      {
        title: 'Real-Time Optimization',
        description: 'Models adjust throughput, recovery and grade continuously, not on a fixed schedule.',
      },
      {
        title: 'Lower Reagent and Energy Use',
        description: 'Advanced process control trims consumption without sacrificing output.',
      },
      {
        title: 'Works With What You Have',
        description: 'Integrates into existing plant control and historian systems.',
      },
    ],
  },
  // Delivered in partnership with HME Solutions (Perth, WA).
  {
    slug: 'fleet-management-live',
    name: 'Fleet Management (FMS Live)',
    tagline: 'Real-time fleet monitoring for open pit and underground mining',
    icon: 'Radar',
    summary:
      'A real-time fleet management system for open pit and underground operations, delivered in partnership with HME Solutions.',
    description:
      'Delivered in partnership with HME Solutions, FMS Live gives control rooms live visibility over every truck, loader and support vehicle across open pit and underground operations. It combines onboard hardware with a live dispatch view so operators can track equipment position, status and performance as it happens.',
    features: [
      'Live equipment position and status tracking',
      'Open pit and underground operation support',
      'Real-time dispatch and control-room visibility',
      'Fully integrated hardware, software, installation and support',
      'Scales from single-pit sites to multi-site fleets',
    ],
    benefits: [
      {
        title: 'One Live View of the Fleet',
        description: 'Control rooms see every vehicle\'s position and status as it happens.',
      },
      {
        title: 'Built for Underground Too',
        description: 'Works across both open pit and underground operations, not just surface fleets.',
      },
      {
        title: 'Fully Integrated Delivery',
        description: 'Hardware, software, installation and support delivered as one package.',
      },
    ],
  },
  {
    slug: 'predictive-asset-analytics',
    name: 'Predictive Asset Analytics (Asset Insight & Asset IQ)',
    tagline: 'AI-driven predictive analytics for mining equipment',
    icon: 'LineChart',
    summary:
      'Predictive analytics platforms that learn from fleet data to flag equipment issues before they cause downtime, delivered in partnership with HME Solutions.',
    description:
      'Asset Insight and Asset IQ, delivered in partnership with HME Solutions, apply algorithm learning to operational data to forecast maintenance needs and flag developing equipment issues early. The result is less manual monitoring and more time spent acting on insights instead of chasing data.',
    features: [
      'Algorithm-driven predictive maintenance insights',
      'Early warning on developing equipment issues',
      'Reduced manual monitoring workload',
      'Fleet-wide performance and reliability trends',
      'Insights that feed directly into maintenance planning',
    ],
    benefits: [
      {
        title: 'Catch Issues Before They Cost You',
        description: 'Predictive alerts flag developing problems ahead of failure.',
      },
      {
        title: 'Less Manual Monitoring',
        description: 'Algorithm learning does the watching so your team doesn\'t have to.',
      },
      {
        title: 'Maintenance Planning, Backed by Data',
        description: 'Reliability trends feed straight into scheduling decisions.',
      },
    ],
  },
  {
    slug: 'mining-communications',
    name: 'Mining Communications (Vital Data Link)',
    tagline: 'Communication and networking infrastructure for mine sites',
    icon: 'Radio',
    summary:
      'Communication and networking infrastructure that keeps fleet and safety data flowing across the mine site, delivered in partnership with HME Solutions.',
    description:
      'Vital Data Link, delivered in partnership with HME Solutions, provides the communication and networking backbone that fleet management, safety and monitoring systems depend on. It keeps data moving reliably between vehicles, sensors and the control room across the mine site.',
    features: [
      'Site-wide vehicle and sensor connectivity',
      'Reliable data links between the field and control room',
      'Foundation for fleet, safety and monitoring systems',
      'Designed for harsh mine-site conditions',
      'Scalable across open pit and underground sites',
    ],
    benefits: [
      {
        title: 'Data That Actually Arrives',
        description: 'A network backbone built for the conditions mine sites throw at it.',
      },
      {
        title: 'One Foundation, Every System',
        description: 'Fleet, safety and monitoring systems all run on the same reliable link.',
      },
      {
        title: 'Scales With the Site',
        description: 'Covers open pit and underground operations as the fleet grows.',
      },
    ],
  },
  {
    slug: 'precision-mining',
    name: 'Precision Mining (Payload & Machine Guidance)',
    tagline: 'Payload systems and machine guidance for precise operations',
    icon: 'Crosshair',
    summary:
      'Payload monitoring and machine guidance systems that improve load accuracy and operating precision, delivered in partnership with HME Solutions.',
    description:
      'Our Precision Mining offering, delivered in partnership with HME Solutions, brings payload systems and machine guidance to haul trucks and loading equipment. Operators get accurate, real-time load and positioning data that reduces overloading, underloading and rework.',
    features: [
      'Real-time payload monitoring on haul trucks',
      'Machine guidance for loading and grading equipment',
      'Reduced overloading, underloading and rework',
      'Operator feedback for consistent load accuracy',
      'Data feeds into fleet-wide production reporting',
    ],
    benefits: [
      {
        title: 'Right Load, Every Time',
        description: 'Real-time payload data reduces overloading and underloading.',
      },
      {
        title: 'Guided Precision',
        description: 'Machine guidance keeps loading and grading equipment on target.',
      },
      {
        title: 'Feeds Straight Into Production Data',
        description: 'Payload and guidance data rolls up into fleet-wide reporting.',
      },
    ],
  },
  {
    slug: 'load-haul-dynamic',
    name: 'Load & Haul Dynamic Trucking Management',
    tagline: 'Dynamic truck-loader assignment and haul cycle management',
    icon: 'Route',
    summary:
      'Dynamic trucking management that assigns and coordinates the load-and-haul cycle in real time, delivered in partnership with HME Solutions.',
    description:
      'Load & Haul Dynamic, delivered in partnership with HME Solutions, manages the truck-and-loader haul cycle dynamically — assigning trucks to loaders and adjusting routes as conditions change, to keep the haul cycle moving efficiently.',
    features: [
      'Dynamic truck-to-loader assignment',
      'Real-time haul cycle coordination',
      'Route adjustment as site conditions change',
      'Reduced queueing and cycle delays',
      'Complements existing fleet management systems',
    ],
    benefits: [
      {
        title: 'The Haul Cycle Keeps Moving',
        description: 'Dynamic assignment adapts to conditions instead of running a fixed plan.',
      },
      {
        title: 'Less Time Queueing',
        description: 'Trucks and loaders are matched in real time to cut delays.',
      },
      {
        title: 'Plays Well With Existing Systems',
        description: 'Complements the fleet management tools already on site.',
      },
    ],
  },
];

export const waymining: Product = {
  name: 'Waymining FMS',
  path: '/waymining',
  tagline: 'Mining fleet & production management software',
  icon: 'Truck',
};

export const getService = (slug?: string): Service | undefined =>
  services.find((s) => s.slug === slug);
